import React from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/card'
import { Button } from '../../../components/button'
import { Progress } from '@joinomu/ui'
import { cn } from '../../../lib/utils'

// JoinOmu Logo Component
function JoinOmuLogo({ className }: { className?: string }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M12 18C12 21.3137 9.31371 24 6 24C2.68629 24 0 21.3137 0 18C0 14.6863 2.68629 12 6 12C9.31371 12 12 14.6863 12 18Z" fill="url(#paint0_linear_202_550)"/>
      <path d="M6 3C6 1.34315 7.34315 0 9 0C10.6569 0 12 1.34315 12 3V12H6V3Z" fill="url(#paint1_linear_202_550)"/>
      <path d="M12 3C12 1.34315 13.3431 0 15 0C16.6569 0 18 1.34315 18 3V12H12V3Z" fill="url(#paint2_linear_202_550)"/>
      <path d="M12 12H24V18C24 21.3137 21.3137 24 18 24C14.6863 24 12 21.3137 12 18V12Z" fill="url(#paint3_linear_202_550)"/>
      <defs>
        <linearGradient id="paint0_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
        <linearGradient id="paint1_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
        <linearGradient id="paint2_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
        <linearGradient id="paint3_linear_202_550" x1="13.1184" y1="12" x2="22.8816" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BBDDFF"/>
          <stop offset="1" stopColor="#C85A15"/>
        </linearGradient>
      </defs>
    </svg>
  )
}

export interface IntroductionProps {
  onStart?: () => void
  onSignInClick?: () => void
  progress?: number
  className?: string
}

export function Introduction({
  onStart,
  onSignInClick,
  progress = 0,
  className
}: IntroductionProps) {

  return (
    <div className={cn("min-h-screen relative", className)}>
      {/* Progress bar and logo fixed to viewport */}
      <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
        <div className="space-y-4">
          <div className="relative">
            <Progress 
              value={progress} 
              className="bg-white/10 [&>div]:bg-gradient-to-r [&>div]:from-[#BBDDFF] [&>div]:to-[#C85A15]"
              style={{
                background: 'linear-gradient(to right, rgba(187, 221, 255, 0.2), rgba(200, 90, 21, 0.2))'
              }}
            />
          </div>
          <a 
            href="https://joinomu.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
          >
            <JoinOmuLogo className="w-6 h-6" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">JoinOmu Healthcare</span>
          </a>
        </div>
      </div>
      {/* Background gradients */}
      <div 
        className="absolute inset-0" 
        style={{
          background: `
            radial-gradient(circle at 20% 30%, rgba(187, 221, 255, 0.3) 0%, transparent 20%),
            radial-gradient(circle at 70% 60%, rgba(200, 90, 21, 0.2) 0%, transparent 20%)
          `
        }}
      ></div>
      
      {/* Center container positioned below logo */}
      <div className="min-h-screen flex justify-center items-center p-4 pt-24">
        <div className="w-full max-w-4xl mx-auto relative z-10">
          <Card className="border border-white/20 dark:border-none bg-white/60 dark:bg-[#0e0e0e]/60 backdrop-blur-md shadow-none min-h-[400px] max-h-[600px] flex flex-col">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl bg-gradient-to-b from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                Welcome to JoinOmu
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-between p-8 pt-4">
              {/* Description and HSA/FSA tag above both sections */}
              <div className="space-y-4 mb-8 text-center">
                <div className="text-muted-foreground text-base leading-relaxed">
                  <p>
                    Take our eligibility quiz, connect with a provider, & create a plan that's right for you.
                  </p>
                </div>
                
                {/* HSA FSA Eligible Tag */}
                <div className="flex justify-center">
                  <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium border border-green-200 dark:border-green-700">
                    HSA/FSA Eligible
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                {/* Left side - Compound medications */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Compound Medications Available</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-3 bg-white/40 dark:bg-black/20 rounded-lg border border-white/30 dark:border-white/10">
                      <span className="font-medium">Semaglutide</span>
                      <span className="text-sm text-muted-foreground">$99/month (all doses)</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white/40 dark:bg-black/20 rounded-lg border border-white/30 dark:border-white/10">
                      <span className="font-medium">Tirzepatide</span>
                      <span className="text-sm text-muted-foreground">$199/month (all doses)</span>
                    </div>
                  </div>
                </div>

                {/* Right side - Name brand medications */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Name Brand Medications Available</h3>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="p-4 bg-white/50 dark:bg-black/30 rounded-lg border border-white/30 dark:border-white/10">
                      <div className="font-medium text-gray-900 dark:text-white">Ozempic</div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-muted-foreground">Semaglutide injection</div>
                        <div className="text-sm text-muted-foreground">Starting at $199/month</div>
                      </div>
                    </div>
                    <div className="p-4 bg-white/50 dark:bg-black/30 rounded-lg border border-white/30 dark:border-white/10">
                      <div className="font-medium text-gray-900 dark:text-white">Zepbound</div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-muted-foreground">Tirzepatide injection</div>
                        <div className="text-sm text-muted-foreground">Starting at $299/month</div>
                      </div>
                    </div>
                    <div className="p-4 bg-white/50 dark:bg-black/30 rounded-lg border border-white/30 dark:border-white/10">
                      <div className="font-medium text-gray-900 dark:text-white">Wegovy</div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-muted-foreground">Semaglutide injection</div>
                        <div className="text-sm text-muted-foreground">Starting at $199/month</div>
                      </div>
                    </div>
                    <div className="p-4 bg-white/50 dark:bg-black/30 rounded-lg border border-white/30 dark:border-white/10">
                      <div className="font-medium text-gray-900 dark:text-white">Wegovy Pill</div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-muted-foreground">Semaglutide pill</div>
                        <div className="text-sm text-muted-foreground">Starting at $149/month</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total cost note */}
              <div className="text-center mt-4">
                <span className="text-sm text-muted-foreground">
                  Total monthly cost: medication price + $79 doctors visit for approval and prescription
                </span>
              </div>

              <div className="space-y-4 mt-8">
                <Button
                  onClick={onStart}
                  className="w-full"
                  size="lg"
                >
                  Start
                </Button>

                <div className="text-center">
                  <span className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Button
                      variant="link"
                      onClick={onSignInClick}
                      className="p-0 h-auto text-sm underline text-muted-foreground"
                    >
                      Sign in
                    </Button>
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}